import React, { createContext, useContext, useRef } from 'react';
import type { CreatorSummary, PitchResearch } from '../services/dataService';

export interface PitchWorkspace {
  research?: PitchResearch | null;
  researchRemoved?: boolean;
  researchInput?: { runId: string; brand: string; platform: string; handle: string } | null;
  step: number;
  creator: CreatorSummary | null;
  brand: string;
  objective: string;
  angle: string;
  notes: string;
  tone: string;
  maxWords: number;
  draftId: string | null;
  subject: string;
  body: string;
  saved: { subject: string; body: string };
  editable: boolean;
  threadId: string | null;
  trackingStatus?: 'active' | 'needs_attention';
  /** When the draft went out via Gmail (the SENT stamp's date); null until it has. */
  sentAt?: string | null;
}

const PitchWorkspaceContext = createContext<React.MutableRefObject<PitchWorkspace | null> | null>(null);

/** One working draft per signed-in workspace. Never write private pitch text
 * to browser storage. MainLayout's account key clears it on sign-out/switch.
 * Navigation preserves edits; reload still requires an explicit server save.
 */
export function PitchWorkspaceProvider({ children }: { children: React.ReactNode }) {
  const workspace = useRef<PitchWorkspace | null>(null);
  return <PitchWorkspaceContext.Provider value={workspace}>{children}</PitchWorkspaceContext.Provider>;
}

export function usePitchWorkspace() {
  const workspace = useContext(PitchWorkspaceContext);
  if (!workspace) throw new Error('Pitch workspace provider is missing');
  return workspace;
}
